/**
 * Time-window filtering + recency-vividness helpers (trail #temporal-
 * insight). Shared by rendersOnMap (source-style.ts), the legacy
 * Leaflet marker/pile loop, and year-bars.ts, so the map render, the
 * pill sentence, and the year-bar strip can never disagree on "what
 * counts as dated" or "which window a record falls in".
 *
 * Honesty rule (matches olm-popup.ts): an OLM record's observed_at is
 * only a REAL date when `date_basis === 'photo_dates'` (the
 * enrich-olm-dates backfill matched actual photo EXIF dates). Every
 * other OLM row carries a fabricated snapshot timestamp (the ingest
 * moment, not an observation moment) — for window-filtering AND
 * vividness purposes that counts as UNDATED, never as "just observed".
 * Every non-OLM source (dc311, reddit, news, curated, user_submission)
 * already carries a real observed_at per the existing whenLine/ageStr
 * logic in explore.tsx, so it's trusted directly.
 */

export type TimeWindow = 'all' | '1y' | '90d';

const DAY_MS = 86_400_000;
const WINDOW_SPAN_MS: Record<Exclude<TimeWindow, 'all'>, number> = {
  '1y': 365 * DAY_MS,
  '90d': 90 * DAY_MS,
};

interface ObsPropsLike {
  source?: { type?: unknown; source_name?: unknown };
  date_basis?: unknown;
  observed_first?: unknown;
  observed_at?: unknown;
}
interface ObsFeatureLike {
  properties?: unknown;
}

/**
 * Narrow duck-type check for "this is an OpenLitterMap record" — kept
 * local (not imported from source-style.ts's sourceKey) to avoid a
 * circular import; same rule sourceKey uses for the 'olm' bucket.
 */
function isOlmRecord(p: ObsPropsLike): boolean {
  const t = p.source?.type;
  if (t === 'user_submission' || t === 'curated' || t === 'news' || t === 'dc311' || t === 'cv_detection') {
    return false;
  }
  const n = String(p.source?.source_name ?? '').toLowerCase();
  return n.includes('openlittermap');
}

function parseMs(v: unknown): number | null {
  if (typeof v !== 'string' || !v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * The record's real "last observed" instant, or null when undated
 * (never-dated legacy rows, or fabricated-snapshot OLM rows).
 */
export function realObservedAtMs(f: ObsFeatureLike): number | null {
  const p = (f.properties ?? {}) as ObsPropsLike;
  if (isOlmRecord(p) && p.date_basis !== 'photo_dates') return null;
  return parseMs(p.observed_at);
}

/**
 * The record's real "first observed" instant (observed_first, falling
 * back to observed_at for single-instant sources), or null when
 * undated. Together with realObservedAtMs this is the [start, end]
 * range a time-window intersects against.
 */
export function realObservedFirstMs(f: ObsFeatureLike): number | null {
  const p = (f.properties ?? {}) as ObsPropsLike;
  if (isOlmRecord(p) && p.date_basis !== 'photo_dates') return null;
  return parseMs(p.observed_first) ?? parseMs(p.observed_at);
}

/** True when the record has no trustworthy date at all. */
export function isUndated(f: ObsFeatureLike): boolean {
  return realObservedAtMs(f) === null;
}

/**
 * Does this record's [observed_first ?? observed_at, observed_at]
 * range intersect the given window? 'all' always matches (including
 * undated records — they only ever disappear under a NARROWER
 * window). A narrower window hides undated records outright; the
 * caller surfaces that as "(+N undated)" rather than silently losing
 * them from every count.
 */
export function inTimeWindow(
  f: ObsFeatureLike,
  window: TimeWindow,
  nowMs: number = Date.now(),
): boolean {
  if (window === 'all') return true;
  const endMs = realObservedAtMs(f);
  if (endMs === null) return false;
  const startMs = realObservedFirstMs(f) ?? endMs;
  const windowStartMs = nowMs - WINDOW_SPAN_MS[window];
  // Range intersection: the record's span and the window's span overlap.
  return startMs <= nowMs && endMs >= windowStartMs;
}

/** Age buckets driving marker/pile vividness. null = full vividness. */
export type AgeBucket = 'aging' | 'stale' | null;

/**
 * fresh (<6mo): null (full vividness, no class needed) · aging
 * (6-24mo): 'aging' (0.75 opacity) · stale (>24mo): 'stale' (0.5
 * opacity + slight desaturation). Undated records get full vividness —
 * we don't have evidence to dim them.
 */
export function ageBucket(ms: number | null, nowMs: number = Date.now()): AgeBucket {
  if (ms === null) return null;
  const ageMonths = (nowMs - ms) / (30 * DAY_MS);
  if (ageMonths < 6) return null;
  if (ageMonths < 24) return 'aging';
  return 'stale';
}

/** CSS class for a plain marker/pile-catcher, or null for full vividness. */
export function ageBucketClassName(f: ObsFeatureLike, nowMs: number = Date.now()): string | null {
  const bucket = ageBucket(realObservedAtMs(f), nowMs);
  return bucket ? `trail-age-${bucket}` : null;
}
